/**
 * Date utilities for the liveflight site.
 * Uses native Date + Intl for formatting — no moment dependency.
 */

const pad = (n) => String(n).padStart(2, "0");

/**
 * Coerce a date-like value to a local Date object.
 * Handles: Date, "YYYY-MM-DD" strings, ISO strings, timestamps.
 * Mimics moment(input).toDate() coercion so callers can pass mixed types safely.
 * @param {string|Date|number} input
 * @returns {Date}
 */
export function toDate(input) {
  if (input instanceof Date) return new Date(input.getTime());
  if (typeof input === "string") {
    // "YYYY-MM-DD" or "YYYY-MM-DDT..." → local midnight (avoids UTC offset from new Date("YYYY-MM-DD"))
    const parts = input.substring(0, 10).split("-");
    if (parts.length === 3) {
      return new Date(+parts[0], parts[1] - 1, +parts[2]);
    }
  }
  return new Date(input);
}

/**
 * Format a Date (or date-like value) to "YYYY-MM-DD" string.
 * Mimics moment(input).format("YYYY-MM-DD").
 * @param {string|Date|number} input
 * @returns {string}
 */
export function toDateString(input) {
  const d = toDate(input);
  const y = d.getFullYear();
  const m = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  return `${y}-${m}-${day}`;
}

// Locale-aware short date formatter (e.g. "sab 15 mar")
const _displayFormatter = new Intl.DateTimeFormat("it-IT", {
  weekday: "short",
  day: "numeric",
  month: "short",
});

/**
 * Format a date for display (locale-aware short format, e.g. "sab 15 mar")
 * @param {string|Date} date - Date in YYYY-MM-DD format, or a Date object
 * @returns {string} Formatted date
 */
export function formatDateDisplay(date) {
  if (!date) return "";
  return _displayFormatter.format(toDate(date));
}

/**
 * Strip time from a Date, returning a new Date at local midnight.
 * @param {Date} date
 * @returns {Date}
 */
export function remove_time_from_date(date = new Date()) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Parse an ISO-style flight-offer datetime string into a structured object.
 * Accepts "YYYY-MM-DDThh:mm:ss" or "YYYY-MM-DDThh:mm".
 * @param {string} flight_offer_date
 * @returns {{
 *   day: string,
 *   hour: string,
 *   day_number: string,
 *   month_number: string,
 *   year: string,
 *   month_string: string,
 *   readable_datetime: string,
 *   friendly_date: string,
 *   friendly_date_short: string,
 *   date: Date
 * }}
 */
export function parse_flight_offers_date(flight_offer_date) {
  let split = flight_offer_date.split("T"),
    day_number = split[0].split("-")[2],
    month_number = split[0].split("-")[1],
    year = split[0].split("-")[0],
    new_date = new Date(year, parseInt(month_number) - 1, day_number),
    month_string = new_date.toLocaleString("default", { month: "long" });

  if (split[1] && split[1].length > 5) split[1] = split[1].slice(0, -3); // rimuove i secondi (:00)

  // yyy-mm-dd to dd-mm-yyyy
  const [hh, mm] = (split[1] || "00:00").split(":").map(Number);
  var d = new Date(year, parseInt(month_number) - 1, day_number, hh, mm);

  let readable_datetime =
    [pad(d.getDate()), pad(d.getMonth() + 1), d.getFullYear()].join("-") +
    " " +
    split[1];

  return {
    day: split[0],
    hour: split[1],
    day_number,
    month_number,
    year,
    month_string,
    readable_datetime,
    friendly_date: day_number + " " + month_string + " " + year,
    friendly_date_short:
      day_number + " " + month_string.slice(0, 3).toUpperCase(),
    date: new_date,
  };
}

/**
 * Outbound + return endpoints lifted off a canonical FlightOffer via the
 * precomputed `firstDeparture` / `lastDeparture` shortcuts (avoids walking
 * itineraries). The `return` slot is empty for one-way offers.
 * @param {object} flight_offer
 * @returns {{departure: string, return: string}}
 */
export function get_flight_offer_dates(flight_offer) {
  return {
    departure: flight_offer.firstDeparture,
    return: flight_offer.oneWay ? "" : flight_offer.lastDeparture,
  };
}

export function timer_display(remaining_ms) {
  const total_seconds = Math.ceil(remaining_ms / 1000);
  const days = Math.floor(total_seconds / 86400);
  const hours = Math.floor((total_seconds % 86400) / 3600);
  const minutes = Math.floor((total_seconds % 3600) / 60);
  const seconds = total_seconds % 60;
  // Drop leading-zero units; mm:ss always renders so the second-level tick is preserved.
  if (days > 0) {
    return `${days}d ${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`;
  }
  if (hours > 0) {
    return `${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${pad(seconds)}s`;
  }
  return `${seconds}s`;
}
